import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Feature } from "geojson";
import { useMap } from "react-map-gl/maplibre";
import { useDispatch } from "react-redux";

import { setCursor } from "../map/mapSlice";

import { EventListeners } from "@/features/geojson_editor/EventListeners";
import {
  type BaseMode,
  createMode,
  type EditorModeType,
} from "@/features/geojson_editor/modes";
import type {
  ChangeCallback,
  GeojsonEditorDelta,
} from "@/features/geojson_editor/modes/BaseMode";
import { usePopupMenu } from "../app/PopupMenuProvider";

type GeojsonChangeCallback = (delta: GeojsonEditorDelta) => void;

type InternalGeojsonEditorContextData = {
  _lastUpdate: number;
  _subscribe: (callback: GeojsonChangeCallback) => void;
  _unsubscribe: (callback: GeojsonChangeCallback) => void;
  _openPopupMenu: (
    content: React.ReactNode,
    position: { x: number; y: number },
  ) => void;
};

type BaseGeojsonEditorContextData = {
  mode: BaseMode;
};

type GeojsonEditorContextReadonlyData = BaseGeojsonEditorContextData & {
  isActive: boolean;
};

type GeojsonEditorContextWritableData = BaseGeojsonEditorContextData & {
  setFeatures: (fs: Feature[]) => void;
  clearFeatures: () => void;
  setMode: (mode: EditorModeType) => void;
  activate: () => void;
  deactivate: () => void;
};

type GeoJSONEditorContextType = InternalGeojsonEditorContextData &
  GeojsonEditorContextWritableData &
  GeojsonEditorContextReadonlyData;

const GeoJSONEditorContext = createContext<GeoJSONEditorContextType | null>(
  null,
);

export const GeoJSONEditorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Custom hooks
  const { map } = useMap();
  const { openPopupMenu } = usePopupMenu();

  // Redux
  const dispatch = useDispatch();

  // State
  const [lastUpdate, setLastUpdate] = useState(0);

  // References
  const isActiveRef = useRef(false);
  const activeComponentsRef = useRef(0);
  const subscribersRef = useRef(new Set<GeojsonChangeCallback>());
  const modeRef = useRef<BaseMode>(null);

  // Function to change the cursor
  const changeCursor = useCallback((cursor: string) => {
    dispatch(setCursor(cursor));
  }, []);

  // Function to inform EditorLayer of changes of its data
  const triggerUpdate = useCallback(() => {
    setLastUpdate(Date.now());
  }, []);

  // Intercept changes to isActive to notify EditorLayer
  const setIsActive = useCallback(
    (isActive: boolean) => {
      isActiveRef.current = isActive;
    },
    [isActiveRef],
  );

  // Function to handle changes of the mode data
  const handleEditorModeDataChanged: ChangeCallback = useCallback(
    (delta, notifyListeners) => {
      if (notifyListeners) {
        subscribersRef.current.forEach((callback) => callback(delta));
      }
      triggerUpdate();
    },
    [],
  );

  // Function to change the mode
  const _changeMode = useCallback(
    (
      mode: EditorModeType | null,
      options: { keepFeatures: boolean } = { keepFeatures: true },
    ) => {
      modeRef.current?.destroy();
      // Create new mode instance using the registry
      const newMode = createMode(
        mode,
        handleEditorModeDataChanged,
        openPopupMenu,
        changeCursor,
      );

      // Transfer current features to new mode
      if (options.keepFeatures) {
        newMode.setFeatures(modeRef.current?._features ?? {});
      }
      modeRef.current = newMode;

      triggerUpdate();
    },
    [handleEditorModeDataChanged, openPopupMenu, changeCursor],
  );

  // Function to change the editor mode without losing its features
  const setMode = useCallback(
    (mode: EditorModeType) => {
      if (!map || !isActiveRef.current) {
        return;
      }

      _changeMode(mode, { keepFeatures: true });
    },
    [map, _changeMode],
  );

  // Function to set the features of the current mode
  const setFeatures = useCallback(
    (fs: Feature[]) => {
      if (!modeRef.current) {
        console.error(
          "[GeoJSONEditorProvider] `setFeatures` called before `setMode`.",
        );
        return;
      }
      if (!map || !isActiveRef.current) {
        return;
      }
      const newDict = fs.reduce((acc: Record<string, Feature>, f: Feature) => {
        acc[f.id as string] = f;
        return acc;
      }, {});
      modeRef.current.setFeatures(newDict);
      triggerUpdate();
    },
    [map],
  );

  // Function to clear the features of the current mode
  const clearFeatures = useCallback(() => {
    if (!map || !isActiveRef.current) return;
    modeRef.current.setFeatures({});
    triggerUpdate();
  }, [map]);

  // Function to subscribe to changes of the current mode
  const subscribe = useCallback((callback: GeojsonChangeCallback) => {
    subscribersRef.current.add(callback);
    return () => subscribersRef.current.delete(callback);
  }, []);

  // Function to unsubscribe from changes of the current mode
  const unsubscribe = useCallback((callback: GeojsonChangeCallback) => {
    subscribersRef.current.delete(callback);
  }, []);

  // Function to initialize the editor
  const initializeEditor = useCallback(() => {
    if (!map) return;

    activeComponentsRef.current += 1;

    if (!isActiveRef.current) {
      setIsActive(true);
    }
    triggerUpdate();
  }, [map]);

  // Function to cleanup the editor
  const cleanupEditor = useCallback(() => {
    activeComponentsRef.current -= 1;

    // Only cleanup when no components are using the editor
    if (activeComponentsRef.current <= 0) {
      activeComponentsRef.current = 0; // Ensure it doesn't go negative
      setIsActive(false);
      _changeMode(null, { keepFeatures: false });
    }
    triggerUpdate();
  }, [_changeMode]);

  // Store the functions in refs to avoid dependency issues
  const initializeEditorRef = useRef(initializeEditor);
  const cleanupEditorRef = useRef(cleanupEditor);

  // Update refs when functions change
  useEffect(() => {
    initializeEditorRef.current = initializeEditor;
    cleanupEditorRef.current = cleanupEditor;
  }, [initializeEditor, cleanupEditor]);

  return (
    <GeoJSONEditorContext.Provider
      value={{
        // Internal state
        _lastUpdate: lastUpdate,
        _openPopupMenu: openPopupMenu,
        _subscribe: subscribe,
        _unsubscribe: unsubscribe,
        activate: initializeEditor,
        clearFeatures,
        deactivate: cleanupEditor,
        // Public state
        isActive: isActiveRef.current,
        mode: modeRef.current,
        setFeatures,
        setMode,
      }}
    >
      {isActiveRef.current && modeRef.current && (
        <EventListeners mode={modeRef.current} />
      )}
      {children}
    </GeoJSONEditorContext.Provider>
  );
};

/**
 * Hook to use the geojson editor.
 */
export const useGeoJSONEditor = (
  onChangeCallback: GeojsonChangeCallback,
  options?: {
    controlled?: boolean;
  },
): GeojsonEditorContextWritableData => {
  const context = useContext(GeoJSONEditorContext);
  if (!context) {
    throw new Error(
      "'useGeoJSONEditor' must be used within a GeoJSONEditorProvider",
    );
  }

  const memoizedOnChangeCallback = useMemo(
    () => onChangeCallback,
    [onChangeCallback],
  );

  // Initialize editor when hook is mounted and cleanup when hook is unmounted
  useEffect(() => {
    context.activate();
    return () => context.deactivate();
  }, []);

  useEffect(() => {
    const onChange: GeojsonChangeCallback = (delta) => {
      if (options?.controlled) {
        context.mode.applyDelta(delta);
      }
      memoizedOnChangeCallback(delta);
    };

    context._subscribe(onChange);
    return () => context._unsubscribe(onChange);
  }, [memoizedOnChangeCallback, options?.controlled]);

  return {
    activate: context.activate,
    clearFeatures: context.clearFeatures,
    deactivate: context.deactivate,
    mode: context.mode,
    setFeatures: context.setFeatures,
    setMode: context.setMode,
  };
};

/**
 * Internal hook to use the geojson editor data. Used only by EditorLayer
 */
export const _useGeoJSONEditorData = (
  onUpdateCallback?: (currentMode: BaseMode) => void,
): GeojsonEditorContextReadonlyData => {
  const context = useContext(GeoJSONEditorContext);
  if (!context) {
    throw new Error(
      "'_useGeoJSONEditorData' must be used within a GeoJSONEditorProvider",
    );
  }

  // Trigger an update of the data when the last update changes
  useEffect(() => {
    if (onUpdateCallback && context.mode) {
      onUpdateCallback(context.mode);
    }
  }, [context._lastUpdate]);

  // Return only the read-only properties
  return {
    isActive: context.isActive,
    mode: context.mode,
  };
};
